import { ServerConfig } from "@/config/config";
import { Ticket } from "@/models/ticket";
import { TicketMessage } from "@/models/ticket-message";
import { AuthService, Axios } from "@/services/auth-service";
import { TicketStatus } from "@/utils/ticket-status";
import { IPaginationData, paginationToQuery } from "@/utils/browse/pagination-data";
import { IPaginatedResponse } from "@/utils/browse/pagination-response";
import { Admin } from "@/models/admin";
import { TicketAdminPin } from "@/models/ticket-admin-pin";
import { TicketAdminBookmark } from "@/models/ticket-admin-bookmark";
import { TicketMessageLike } from "@/models/ticket-message-like";
import { TicketReplyTemplate } from "@/models/ticket-reply-template";
import { TicketFilters } from "@/utils/ticket-filters";
import { AuthSocketService } from "./auth-socket-service";
import {TicketReadList} from "@/models/ticket-read-list";

export interface INewTicketData {
	clientId:number;
	dossierId:number;
	subject:string;
	departmentId:number;
	adminIds:number[];
	attentionAdminIds:number[];
	internal:boolean;
	message:string;
	timeSheduled:Date;
}

export interface INewResponseData {
	isInternal:boolean;
	isInterraction: boolean;
	content:string;
	attentions?:number[];
	attachements:File[];
	notifyUser:boolean;
}


export interface GlobalSearch {
	tickets: Ticket[];
	messages: TicketMessage[];
}

export class TicketServiceClass{
	private readonly url:string = `${ServerConfig.host}/admin/tickets`;
	private readonly replyTemplatesUrl:string = `${this.url}/reply-templates`
	private readonly globalSearchUrl:string = `${ServerConfig.host}/admin/tickets/global`;

	storedFilters:TicketFilters | null = null;
	storedPagination:IPaginationData | null = null;
	lastUsedTab:number = 0;
	public storedRecords:number = 0;
	public currentIndex:number = 0;

	private unreadAttentionsEstimate:number = 0;
	private unreadLikesEstimate:number = 0;
	public getUnreadAttentionsEstimate():number {
		return this.unreadAttentionsEstimate;
	}
	public getUnreadLikesEstimate():number {
		return this.unreadLikesEstimate;
	}

	async getTickets(pagination:IPaginationData, quickFilters:TicketFilters, browseName:string = ""):Promise<IPaginatedResponse<Ticket>>{
		this.storedPagination = pagination;
		this.storedFilters = quickFilters;
		let response = await this.fetchTickets(pagination, quickFilters, browseName);
		this.storedRecords = response.records;
		return response;
	}

	private async fetchTickets(pagination:IPaginationData, quickFilters:TicketFilters, browseName:string = ""):Promise<IPaginatedResponse<Ticket>> {
		let response = await Axios.get(`${this.url}`, {
			params: {
				...paginationToQuery(pagination),
				...quickFilters.toUrlQueryParam(),
				browseName
			}
		});
		return {
			records: response.data.records,
			data: response.data.data.map((t:any)=>new Ticket(t))
		};
	}

	async searchGlobal(search:string):Promise<GlobalSearch>{
		let response = await Axios.get(`${this.globalSearchUrl}`, {
			params: {
				search
			}
		});

		let m = response.data.Messages.map((r:TicketMessage) => new TicketMessage(r));
		let t = response.data.Tickets.map((r:Ticket) => new Ticket(r));

		return {
			tickets: t,
			messages: m
		};
	}

	hasNext():boolean {
		if (!this.storedFilters) return false;
		if (!this.storedPagination) return false;
		if (!this.storedRecords) return false;
		if (this.storedFilters.read === false && this.storedRecords > 0) {
			return true;
		}
		if (this.currentIndex + 1 >= this.storedRecords) return false;
		return true;
	}

	hasPrevious():boolean {
		if (!this.storedFilters) return false;
		if (!this.storedPagination) return false;
		if (!this.storedRecords) return false;
		if (this.currentIndex <= 0) return false;
		return true;
	}

	async nextId():Promise<number>{
		if (!this.storedFilters || !this.storedPagination || !this.storedRecords) throw new Error("No next ticket: Missing data");
		if (this.currentIndex >= this.storedRecords) throw new Error("No next ticket: End of list");
		let pagination = {...this.storedPagination};
		pagination.limit = 1;
		pagination.offset = this.currentIndex;
		if (this.storedFilters.read === undefined) {
			pagination.offset = this.currentIndex+1;
		}
		let response = await this.fetchTickets(pagination, this.storedFilters, "");
		this.currentIndex = pagination.offset;
		if (response.data.length == 0){
			return 0;
		}
		return response.data[0].id;
	}

	async previousId():Promise<number>{
		if (!this.storedFilters || !this.storedPagination || !this.storedRecords) throw new Error("No previous ticket: Missing data");
		if (this.currentIndex <= 0) throw new Error("No previous ticket: End of list");
		let pagination = {...this.storedPagination};
		pagination.limit = 1;
		pagination.offset = this.currentIndex-1;
		if (pagination.offset < 0) {
			pagination.offset = 0;
		}
		let response = await this.fetchTickets(pagination, this.storedFilters, "");
		this.currentIndex = pagination.offset;
		if (response.data.length == 0){
			return 0;
		}
		return response.data[0].id;
	}

	async getUnreadTicketCountsPerQuery(filters:TicketFilters[]):Promise<number[]>{
		let response = await Axios.get(`${this.url}/unread-counts-per-query`, {
			params: {
				filters: JSON.stringify(filters.map(f=>f.toUrlQueryParam(true)))
			}
		});
		return response.data as number[];
	}

	async getTicket(id:number):Promise<Ticket> {
		let result = await Axios.get(`${this.url}/${id}`);
		return new Ticket(result.data);
	}

	async createTicket(ticketData:INewTicketData):Promise<Ticket> {
		let result = await Axios.post(`${this.url}`, ticketData);
		return new Ticket(result.data);
	}

	async setDepartment(ticketId:number, departmentId:number):Promise<Ticket> {
		let result = await Axios.post(`${this.url}/${ticketId}/set-department/${departmentId}`);
		return new Ticket(result.data);
	}

	async setStatus(ticketId:number, statusId:TicketStatus):Promise<Ticket> {
		let result = await Axios.post(`${this.url}/${ticketId}/set-status/${statusId}`);
		return new Ticket(result.data);
	}

	async sendResponse(ticketId:number, data:INewResponseData):Promise<TicketMessage> {
		let formData = new FormData();
		formData.append("content", data.content);
		formData.append("isInternal", data.isInternal ? "true" : "false");
		formData.append("isInterraction", data.isInterraction ? "true" : "false");
		data.attachements.forEach((file, i) => {
			formData.append(`file${i}`, file, file.name);
		});
		formData.append("fileCount", `${data.attachements.length}`);
		if (data.attentions) {
			formData.append("attentions", JSON.stringify(data.attentions));
		}
		formData.append("notifyUser", `${data.notifyUser}`);
		let result = await Axios.post(`${this.url}/${ticketId}/send-message`, formData);
		return new TicketMessage(result.data);
	}

	async markAllAttentionsOfUserAsRead(ticketId:number):Promise<Ticket> {
		let result = await Axios.post(`${this.url}/${ticketId}/mark-all-my-attentions-as-read`);
		return new Ticket(result.data);
	}

	async markAttentionAsRead(ticketId:number, msgId:number, attentionId:number):Promise<TicketMessage> {
		let result = await Axios.post(`${this.url}/${ticketId}/${msgId}/mark-attention-as-read/${attentionId}`);
		return new TicketMessage(result.data);
	}

	async markAttentionAsUnRead(ticketId:number, msgId:number, attentionId:number):Promise<TicketMessage> {
		let result = await Axios.post(`${this.url}/${ticketId}/${msgId}/mark-attention-as-unread/${attentionId}`);
		return new TicketMessage(result.data);
	}

	async updateAttentions(ticket: Ticket, id: Number, attentions: number[]): Promise<void> {
		let formData = new FormData();
		formData.append("attentions", JSON.stringify(attentions));
		let result = await Axios.post(`${this.url}/${ticket.id}/${id}/update-attentions`, formData);
		new TicketMessageLike(result.data);
	}

	async markDepartmentAttentionAsRead(ticketId:number, msgId:number):Promise<TicketMessage> {
		let result = await Axios.post(`${this.url}/${ticketId}/${msgId}/mark-departement-as-read`);
		return new TicketMessage(result.data);
	}

	async setAssignedAdmins(ticketId:number, adminIds:number[]):Promise<Ticket> {
		let result = await Axios.post(`${this.url}/${ticketId}/set-assigned-admins`, adminIds);
		return new Ticket(result.data);
	}

	async makeTicketVisibleForClient(ticketId:number):Promise<Ticket>  {
		return this.setVisibleForClient(ticketId, true);
	}

	async setVisibleForClient(ticketId:number, isVisible:boolean):Promise<Ticket>  {
		let result = await Axios.post(`${this.url}/${ticketId}/set-visible/${isVisible}`);
		return new Ticket(result.data);
	}

	async toggleTicketMessageInternal(ticketId:number, msgId:number):Promise<TicketMessage>  {
		let result = await Axios.post(`${this.url}/${ticketId}/${msgId}/toggle-internal`);
		return new TicketMessage(result.data);
	}

	async markTicketAsRead(ticketId:number):Promise<Ticket> {
		let result = await Axios.post(`${this.url}/${ticketId}/mark-as-read`);
		return new Ticket(result.data);
	}

	async deleteLastMessage(ticketId:number):Promise<TicketMessage>{
		let result = await Axios.delete(`${this.url}/${ticketId}/last-message`);
		return new TicketMessage(result.data);
	}

	async deleteSelectedTicketMessage(ticketId:number, msgId:number):Promise<TicketMessage>{
		let result = await Axios.delete(`${this.url}/${ticketId}/${msgId}`);
		return new TicketMessage(result.data);
	}

	async updateTicketMessage(ticketId:number, msgId:number, msgContent:string):Promise<TicketMessage>{
		let result = await Axios.put(`${this.url}/${ticketId}/${msgId}`, {message: msgContent});
		return new TicketMessage(result.data);
	}

	async setTicketPriority(ticketId:number, priority:number):Promise<Ticket>{
		let result = await Axios.post(`${this.url}/${ticketId}/set-priority/${priority}`);
		return new Ticket(result.data);
	}

	async sheduleTicket(ticketId:number, date:Date):Promise<Ticket>{
		let result = await Axios.post(`${this.url}/${ticketId}/shedule/${Math.round(date.getTime() / 1000)}`);
		return new Ticket(result.data);
	}

	async fetchOpenedAdmins(ticket:Ticket):Promise<void>{
		let result = await Axios.get(`${this.url}/${ticket.id}/opened-admins`);
		ticket.openedAdmins = (result.data as any[]).map((t:any)=>new Admin(t));
	}

	async pin(ticket:Ticket):Promise<void>{
		let result = await Axios.post(`${this.url}/${ticket.id}/pin`);
		let pin = new TicketAdminPin(result.data);
		ticket.adminPins.push(pin);
	}

	async unpin(ticket:Ticket):Promise<void>{
		await Axios.post(`${this.url}/${ticket.id}/unpin`);
		if (!AuthService.user) return;
		for (let i = ticket.adminPins.length -1; i>=0;i--){
			if (ticket.adminPins[i].adminId == AuthService.user.id) {
				ticket.adminPins.splice(i, 1);
			}
		}
	}

	async bookmark(ticket:Ticket):Promise<void>{
		let result = await Axios.post(`${this.url}/${ticket.id}/bookmark`);
		let bookmark = new TicketAdminBookmark(result.data);
		ticket.adminBookmarks.push(bookmark);
	}

	async unBookmark(ticket:Ticket):Promise<void>{
		await Axios.post(`${this.url}/${ticket.id}/unbookmark`);
		if (!AuthService.user) return;
		for (let i = ticket.adminBookmarks.length -1; i>=0;i--){
			if (ticket.adminBookmarks[i].adminId == AuthService.user.id) {
				ticket.adminBookmarks.splice(i, 1);
			}
		}
	}

	async like(ticket:Ticket, message:TicketMessage):Promise<void>{
		let result = await Axios.post(`${this.url}/${ticket.id}/${message.id}/like`);
		new TicketMessageLike(result.data);
		//message.likes.push(like);

	}

	async unlike(ticket:Ticket, message:TicketMessage):Promise<void>{
		await Axios.post(`${this.url}/${ticket.id}/${message.id}/un-like`);
		if (!AuthService.user) return;
		for (let i = message.likes.length -1; i>=0;i--){
			if (message.likes[i].adminId == AuthService.user.id) {
				message.likes.splice(i, 1);
			}
		}
	}

	async amountOfAssignedTickets():Promise<number>{
		let result = await Axios.get(`${this.url}/count-assigned-to-me`);
		return parseInt(result.data);
	}

	async amountOfAttentionedTickets():Promise<number>{
		let result = await Axios.get(`${this.url}/count-my-attentions`);
		return parseInt(result.data);
	}


	async getReplyTemplates():Promise<TicketReplyTemplate[]>{
		let result = await Axios.get(`${this.replyTemplatesUrl}`);
		return result.data.map((r:any)=>new TicketReplyTemplate(r));
	}

	async createReplyTemplate(template:TicketReplyTemplate):Promise<TicketReplyTemplate>{
		let result = await Axios.post(`${this.replyTemplatesUrl}`, template.getJSON());
		return new TicketReplyTemplate(result.data);
	}

	async updateReplyTemplate(template:TicketReplyTemplate):Promise<TicketReplyTemplate>{
		let result = await Axios.put(`${this.replyTemplatesUrl}/${template.id}`, template.getJSON());
		return new TicketReplyTemplate(result.data);
	}

	async deleteReplyTemplate(template:TicketReplyTemplate):Promise<void>{
		await Axios.delete(`${this.replyTemplatesUrl}/${template.id}`);
	}

	async computeTemplate(ticketId:number, text:string):Promise<string>{
		let result = await Axios.post(`${this.url}/${ticketId}/compute-template-text`, {text});
		return result.data.result;
	}

	async fetchReadList(date: Date):Promise<TicketReadList[]>{
		let result = await Axios.get(`${this.url}/read-list?date=` + `${date.getFullYear()}-${date.getMonth()+1}-${date.getDate()}`);
		return result.data.map((r:any)=>new TicketReadList(r));
	}

	async getUnreadAttentionsCount(adminId?:number):Promise<number>{
		let url = `${this.url}/count-unread-attentions`;
		if (adminId){
			url = `${this.url}/count-unread-attentions/${adminId}`;
		}
		let result = await Axios.get(url);
		this.unreadAttentionsEstimate = result.data;
		return this.unreadAttentionsEstimate;
	}

	async getUnreadLikesCount(adminId?:number):Promise<number>{
		let url = `${this.url}/count-unread-attentions`;
		if (adminId){
			url = `${this.url}/count-unread-attentions/${adminId}`;
		}
		let result = await Axios.get(url);
		this.unreadAttentionsEstimate = result.data;
		return this.unreadAttentionsEstimate;
	}

	async updateSubject(ticketId:number, newSubject:string):Promise<Ticket>{
		let result = await Axios.post(`${this.url}/${ticketId}/change-subject`, {subject: newSubject});
		return new Ticket(result.data);
	}


	registerSocketEvents(){
		// Refresh unread attentions
		let attentionReadEventId = 0;
		AuthSocketService.on("/attention-read", ()=>{
			this.unreadAttentionsEstimate--;
			let thisEventId = Math.random();
			attentionReadEventId = thisEventId;
			setTimeout(()=>{
				if (attentionReadEventId != thisEventId) return;
				this.getUnreadAttentionsCount();
			},5000);
		});
		AuthSocketService.on("/attention-unread", ()=>{
			this.unreadAttentionsEstimate++;
			let thisEventId = Math.random();
			attentionReadEventId = thisEventId;
			setTimeout(()=>{
				if (attentionReadEventId != thisEventId) return;
				this.getUnreadAttentionsCount();
			},5000);
		});
	}

	async init(){
		this.getUnreadAttentionsCount();
		this.getUnreadLikesCount();
	}
};

export const TicketService = new TicketServiceClass();